import { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import Icons from '../../../../../assets/index';
import CustomTextInput from '../../InputFields/CustomTextInput';

type Column = {
  label: string;
  dataType: 'Text Input' | 'Number' | 'Date' | 'Dropdown';
};

type TableData = {
  columns: Column[];
  rows: string[];
  groupConfig: {
    groupName: string;
    selectedColumns: number[];
  };
  tabName?: string;
};

type TableDataTypeProps = {
  numColumns?: number;
  numRows?: number;
  showRowConfig?: boolean;
  showColumnConfig?: boolean;
  showGroupConfig?: boolean;
  onChange?: (data: TableData) => void;
  initialData?: TableData;
  errors?: { [key: string]: string };
  isPopulateData?: boolean;
};

const TableDataType: React.FC<TableDataTypeProps> = ({
  numColumns = 4,
  numRows = 4,
  showRowConfig = true,
  showColumnConfig = true,
  showGroupConfig = true,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange = () => {},
  initialData,
  errors = {},
  isPopulateData,
}) => {
  const [columnConfigExpanded, setColumnConfigExpanded] = useState(true);
  const [rowConfigExpanded, setRowConfigExpanded] = useState(true);
  const [groupConfigExpanded, setGroupConfigExpanded] = useState(true);

  // Initialize columns state - handle properly when numColumns is 1
  const [columns, setColumns] = useState<Column[]>(() => {
    if (initialData?.columns && initialData.columns?.length > 0) {
      return initialData.columns;
    }
    // Create an array with the required number of column objects
    return Array(numColumns)
      .fill(null)
      .map(() => ({
        label: '',
        dataType: '' as Column['dataType'],
      }));
  });

  // Initialize rows state - handle properly when numRows is 1
  const [rows, setRows] = useState<string[]>(() => {
    if (initialData?.rows && initialData.rows?.length > 0) {
      return initialData.rows;
    }
    // Create an array with the required number of empty strings
    return Array(numRows).fill('');
  });

  const [groupName, setGroupName] = useState(initialData?.groupConfig?.groupName || '');
  const [selectedColumns, setSelectedColumns] = useState<number[]>(
    initialData?.groupConfig?.selectedColumns || []
  );

  const [selectAll, setSelectAll] = useState(
    (initialData?.groupConfig?.selectedColumns?.length ?? 0) > 0 &&
      initialData?.groupConfig?.selectedColumns?.length ===
        columns.filter((_, idx) => idx > 0)?.length
  );

  const [columnsDropdownOpen, setColumnsDropdownOpen] = useState(false);
  const [tabName, setTabName] = useState(initialData?.tabName || '');

  // Notify parent component of all changes
  const notifyParent = () => {
    onChange({
      columns,
      rows,
      groupConfig: {
        groupName,
        selectedColumns,
      },
      tabName,
    });
  };

  // Handle column count changes
  useEffect(() => {
    if (columns?.length !== numColumns) {
      if (columns?.length < numColumns) {
        // Add columns
        const additionalColumns = Array(numColumns - columns?.length)
          .fill(null)
          .map(() => ({
            label: '',
            dataType: '' as Column['dataType'],
          }));

        setColumns((prevColumns) => [...prevColumns, ...additionalColumns]);
      } else {
        // Remove excess columns
        setColumns((prevColumns) => prevColumns.slice(0, numColumns));
      }
    }
  }, [numColumns]);

  // Handle row count changes
  useEffect(() => {
    if (rows?.length !== numRows) {
      if (rows?.length < numRows) {
        const newRows = Array(numRows - rows?.length).fill('');
        setRows((prevRows) => [...prevRows, ...newRows]);
      } else {
        setRows((prevRows) => prevRows.slice(0, numRows));
      }
    }
  }, [numRows]);

  // Notify parent when any data changes
  useEffect(() => {
    notifyParent();
  }, [columns, rows, groupName, selectedColumns, tabName]);

  const handleColumnNameChange = (index: number, value: string) => {
    setColumns((prevColumns) => {
      const newColumns = [...prevColumns];
      newColumns[index] = { ...newColumns[index], label: value };
      return newColumns;
    });
  };

  const handleColumnTypeChange = (index: number, value: Column['dataType']) => {
    setColumns((prevColumns) => {
      const newColumns = [...prevColumns];
      newColumns[index] = { ...newColumns[index], dataType: value };
      return newColumns;
    });
  };

  const handleRowNameChange = (index: number, value: string) => {
    setRows((prevRows) => {
      const updatedRows = [...prevRows];
      updatedRows[index] = value;
      return updatedRows;
    });
  };

  const handleTabNameChange = (value: string) => {
    setTabName(value);
  };

  interface HandleColumnSelectParams {
    index: number;
  }

  const handleColumnSelect = ({ index }: HandleColumnSelectParams): void => {
    setSelectedColumns((prev) => {
      const newSelection = [...prev];

      if (newSelection.includes(index)) {
        const filteredSelection = newSelection.filter((i) => i !== index);
        setSelectAll(false);
        return filteredSelection;
      } else {
        newSelection.push(index);

        // Check if all columns are now selected
        const allColumnsSelected = columns.every(
          (_, idx) => idx === 0 || newSelection.includes(idx)
        );
        setSelectAll(allColumnsSelected);

        return newSelection;
      }
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedColumns([]);
      setSelectAll(false);
    } else {
      setSelectedColumns(columns.map((_, index) => index).filter((index) => index >= 0));
      setSelectAll(true);
    }
  };

  const toggleDropdown = () => {
    setColumnsDropdownOpen(!columnsDropdownOpen);
  };

  return (
    <div className="w-full p-2 h-full">
      <div className="border h-90vh">
        {/* Column Configuration */}
        {showColumnConfig && (
          <div className="rounded">
            <div
              className={`p-3 border-b flex justify-between items-center cursor-pointer  ${columnConfigExpanded ? 'bg-gray-50' : 'bg-[#FFFFFF]'}`}
              onClick={() => setColumnConfigExpanded(!columnConfigExpanded)}
            >
              <h3 className="font-medium">Column Configuration</h3>
              {columnConfigExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>
            {columnConfigExpanded && (
              <div className="p-3 max-h-64 overflow-y-auto">
                {columns.map((col, index) => (
                  <div key={index} className="mb-4">
                    <div className="flex  mb-2">
                      <label className="block text-sm font-medium text-gray-500 w-[60%] ">
                        Column {index + 1} {index < 3 && <span className="text-ds-red-400">*</span>}
                      </label>
                      <label className="block text-sm font-medium text-gray-500  w-[40%]">
                        Column Type {index < 3 && <span className="text-ds-red-400">*</span>}
                      </label>
                    </div>
                    <div className="flex gap-2 relative">
                      <input
                        type="text"
                        placeholder="Enter column name..."
                        className={`w-[60%] p-2 border-2 rounded-md outline-none text-ds-gray-500  text-sm placeholder:text-ds-gray-300 ${
                          errors[`column_${index}`] ? 'border-ds-red-400' : 'border-ds-gray-100'
                        }`}
                        value={col.label}
                        onChange={(e) => handleColumnNameChange(index, e.target.value)}
                      />
                      <select
                        className={`w-[40%] p-2 border-2 rounded-md bg-white appearance-none text-ds-gray-500  outline-none text-sm placeholder:text-ds-gray-300 ${
                          errors[`columnType_${index}`] ? 'border-ds-red-400' : 'border-ds-gray-100'
                        } `}
                        value={col.dataType}
                        onChange={(e) =>
                          handleColumnTypeChange(index, e.target.value as Column['dataType'])
                        }
                      >
                        <option value="" disabled hidden>
                          Select
                        </option>
                        <option value="string">Text Input</option>
                        <option value="number"> Number Input</option>
                        <option value="date">Date Selection</option>
                      </select>
                      <div className="absolute inset-y-0 right-2 flex items-center pointer-events-none text-gray-500">
                        <Icons.arrowDownIcon />
                      </div>
                    </div>
                    <div className="flex">
                      <div className="w-[60%]">
                        {errors[`column_${index}`] && (
                          <p className="text-ds-red-400 text-xs mt-1">
                            {errors[`column_${index}`]}
                          </p>
                        )}
                      </div>
                      <div className="w-[40%]">
                        {errors[`columnType_${index}`] && (
                          <p className="text-ds-red-400 text-xs mt-1">
                            {errors[`columnType_${index}`]}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Row Configuration */}
        {showRowConfig && (
          <div className="rounded">
            <div
              className={`p-3 border-b flex justify-between items-center cursor-pointer ${rowConfigExpanded ? 'bg-gray-50' : 'bg-[#FFFFFF]'}`}
              onClick={() => setRowConfigExpanded(!rowConfigExpanded)}
            >
              <h3 className="font-medium">Row Configuration</h3>
              {rowConfigExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>
            {rowConfigExpanded && (
              <div className="p-3 max-h-64 overflow-y-auto">
                {rows.map((row, index) => (
                  <div key={index} className="mb-4">
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      Row {index + 1} <span className="text-ds-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Please enter row header name"
                      className={`w-[80%] p-2 border-2 rounded-md outline-none text-sm text-ds-gray-500 placeholder:text-ds-gray-300 ${
                        errors[`row_${index}`] ? 'border-ds-red-400' : 'border-ds-gray-100'
                      }`}
                      value={row}
                      onChange={(e) => handleRowNameChange(index, e.target.value)}
                    />
                    {errors[`row_${index}`] && (
                      <p className="text-ds-red-400 text-xs mt-1">{errors[`row_${index}`]}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Group Configuration */}
        {showGroupConfig && (
          <div className="rounded">
            <div
              className={`p-3 border-b flex justify-between items-center cursor-pointer ${groupConfigExpanded ? ' bg-gray-50' : 'bg-[#FFFFFF]'}`}
              onClick={() => setGroupConfigExpanded(!groupConfigExpanded)}
            >
              <h3 className="font-medium">Group Configuration</h3>
              {groupConfigExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>
            {groupConfigExpanded && (
              <div className="p-3 space-y-4">
                {/* Group Name Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    Group Name <span className="text-ds-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    className={`w-[80%] p-2 border-2 rounded-md outline-none text-ds-gray-500 text-sm placeholder:text-ds-gray-300 ${
                      errors.groupName ? 'border-ds-red-400' : 'border-ds-gray-100'
                    }`}
                    placeholder="Enter group name"
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                  />
                  {errors.groupName && (
                    <p className="text-ds-red-400 text-xs mt-1">{errors.groupName}</p>
                  )}
                </div>

                {/* Applicable Columns Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    Applicable Columns <span className="text-ds-red-400">*</span>
                  </label>

                  {/* Dropdown UI */}
                  <div className="relative">
                    <div
                      className={` w-[80%] border-2 rounded p-2 cursor-pointer bg-white flex justify-between items-center ${
                        errors.selectedColumns ? 'border-ds-red-400' : 'border-ds-gray-100'
                      }`}
                      onClick={toggleDropdown}
                    >
                      <span>
                        {selectedColumns?.length > 0
                          ? `${selectedColumns?.length} column${
                              selectedColumns?.length > 1 ? 's' : ''
                            } selected`
                          : 'Select'}
                      </span>
                      {columnsDropdownOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </div>
                    {errors.selectedColumns && (
                      <p className="text-ds-red-400 text-xs p-2 border-t">
                        {errors.selectedColumns}
                      </p>
                    )}

                    {columnsDropdownOpen && (
                      <div className="absolute z-10 w-[80%] bg-white shadow-lg max-h-60 overflow-y-auto mt-1 border rounded">
                        {/* All Columns Toggle */}
                        <div className="p-2 border-b">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <div className="relative inline-block">
                              <input
                                type="checkbox"
                                checked={selectAll}
                                onChange={handleSelectAll}
                                className="opacity-0 absolute h-5 w-5 cursor-pointer outline-none"
                              />
                              <div
                                className={`flex items-center justify-center ${
                                  selectAll
                                    ? 'border-green-500'
                                    : selectedColumns.length > 0
                                      ? 'border-green-500'
                                      : 'border-gray-300'
                                }`}
                              >
                                {selectAll ? (
                                  <Icons.checkIcon />
                                ) : selectedColumns.length > 0 ? (
                                  <Icons.minusBoxIcon />
                                ) : (
                                  <Icons.borderSquareIcon />
                                )}
                              </div>
                            </div>
                            <span>All Columns</span>
                          </label>
                        </div>

                        {/* Individual Columns Toggle */}
                        {columns.map(
                          (col, index) =>
                            col.label && (
                              <div key={index} className="  px-2 hover:bg-gray-50 border-b py-3">
                                <label className="flex items-center space-x-2 cursor-pointer">
                                  <div className="relative inline-block">
                                    <input
                                      type="checkbox"
                                      checked={selectedColumns.includes(index)}
                                      onChange={() => handleColumnSelect({ index })}
                                      className="opacity-0 absolute h-5 w-5 cursor-pointer"
                                    />
                                    <div
                                      className={`flex items-center justify-center gap-2 mr-1 mt-1 ${
                                        selectedColumns.includes(index)
                                          ? 'border-green-500'
                                          : 'border-gray-300'
                                      }`}
                                    >
                                      {selectedColumns.includes(index) ? (
                                        <Icons.checkIcon />
                                      ) : (
                                        <Icons.borderSquareIcon />
                                      )}
                                    </div>
                                  </div>
                                  <span className="text-sm text-ds-gray-500">{`${col.label} (Column ${index + 1})`}</span>
                                </label>
                              </div>
                            )
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {isPopulateData && (
        <div className="mt-4">
          <CustomTextInput
            label="Data Identifier Name"
            name="tabName"
            value={tabName}
            onChange={(e) => handleTabNameChange(e.target.value)}
            error={errors.tabName}
            className="text-ds-gray-500 font-semibold text-sm"
            placeholder="Enter data identifier name"
          />
        </div>
      )}
    </div>
  );
};

export default TableDataType;
